import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import numeral from 'numeral';
import { useHyperFormulaNote } from '../hooks/useHyperFormulaNote';
import { handleXBRLFieldBlur } from '../utils/xbrlMandatoryFields';
import { applyHideEmptyColumns, toggleColumnVisibilityForSection } from '../utils/columnVisibility';

interface NoteShareRelatedPartyTableProps {
  section: any;
  companyInfo: {
    consolidated: string;
    first_year: string;
    // Support both structures
    reporting_period?: {
      opening: string;
      start: string;
      end: string;
    };
    currentPeriod?: {
      opening: string;
      start: string;
      end: string;
    };
  };
  negativeTargets?: string[];
  formData?: Record<string, any>; // Add formData prop to receive data from parent
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
}

// ... (keep PeriodInfo and collectMembers as is)

// ... (keep formatDateForContext and formatDateForDisplay as is)

// ... (keep getContextRef as is)

// ... (keep NoteFinancialInput as is)

// ... (keep LineItemProps and NoteLineItem as is)

// ... (inside NoteShareRelatedPartyTable component)

interface PeriodInfo {
  opening: string;
  start: string;
  end: string;
}

// Recursive function to collect all members (same as SOCIE)
const collectMembers = (member: any, allMembers: any[] = []): any[] => {
  if (member.structure?.children) {
    member.structure.children.forEach((child: any) => {
      collectMembers(child, allMembers);
    });
  }

  // Exclude certain member types
  if (member.name &&
    !['ClassesOfShareCapitalMember', 'EntitysTotalForRelatedPartiesMember'].includes(member.name)) {
    if (!allMembers.find(m => m.name === member.name)) {
      allMembers.push(member);
    }
  }

  return allMembers;
};

// Helper to format date strings
const formatDateForContext = (dateStr: string): string => {
  if (/^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  return dateStr.replace(/-/g, '');
};

// Helper to format date for display
const formatDateForDisplay = (dateStr: string): string => {
  if (/^\d{8}$/.test(dateStr)) {
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
  }
  return dateStr;
};

// Get context ref (same logic as SOCIE but no vertical tracking)
const getContextRef = (
  item: any,
  periodInfo: PeriodInfo,
  memberSuffix: string,
  memberName?: string,
  isOpening: boolean = false
): string => {
  const formattedEnd = formatDateForContext(periodInfo.end);
  const formattedStart = formatDateForContext(periodInfo.start);
  const formattedOpening = formatDateForContext(periodInfo.opening);

  const contextPart = isOpening ? `asof_${formattedOpening}` :
    (item.properties?.period_type === 'duration' ?
      `fromto_${formattedStart}_${formattedEnd}` :
      `asof_${formattedEnd}`);

  if (memberName) {
    return `${contextPart}_${memberSuffix}_${memberName}`;
  } else {
    return `${contextPart}_${memberSuffix}`;
  }
};

// Financial input component (simpler than SOCIE - no vertical fields)
const NoteFinancialInput: React.FC<{
  item: any;
  contextRef: string;
  fieldId: string;
  fieldName: string;
  isReadonly: boolean;
  value: string;
  onFieldChange: (name: string, contextRef: string, value: string, qname?: string) => void;
  specialAttributes: Record<string, string>;
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
}> = ({ item, contextRef, fieldId, fieldName, isReadonly, value, onFieldChange, specialAttributes, onFootnoteClick }) => {
  const [computedStyle, setComputedStyle] = useState<React.CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Format value for display
  const formatValue = useCallback((val: string, isReadonlyField: boolean): string => {
    if (!val || val.trim() === '') {
      return isReadonlyField ? '0.00' : '';
    }

    const numericValue = numeral(val).value();
    if (numericValue === null || isNaN(numericValue)) {
      return isReadonlyField ? '0.00' : '';
    }

    return numeral(numericValue).format('0,0.00');
  }, []);

  // Format initial value
  const initialValue = useMemo(() => {
    if (!value || value.trim() === '') return '';
    return formatValue(value, false);
  }, [value, formatValue]);

  // Apply styling based on readonly status (set by HyperFormula hook)
  useEffect(() => {
    const checkReadonly = () => {
      if (inputRef.current && inputRef.current.readOnly) {
        setComputedStyle({ backgroundColor: 'var(--bg-white-dark)', color: '#495057', fontWeight: 'bold' });
      }
    };

    const timer = setTimeout(checkReadonly, 600);
    return () => clearTimeout(timer);
  }, []);

  const handleFocus = useCallback(() => {
    // For uncontrolled editable inputs, show raw numeric value when focused (remove formatting)
    if (inputRef.current && !inputRef.current.readOnly) {
      const currentValue = inputRef.current.value || '';
      const numericValue = numeral(currentValue).value();
      if (numericValue !== null) {
        inputRef.current.value = numericValue.toString();
      }
    }
  }, []);

  const handleBlur = useCallback(() => {
    // CRITICAL: Clear any pending debounced update and process immediately
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Format the value on blur and update ref immediately
    if (inputRef.current && !inputRef.current.readOnly) {
      // Check XBRL mandatory fields first (set 0.00 if empty)
      handleXBRLFieldBlur(inputRef.current);
      
      const currentValue = inputRef.current.value || '';
      const formatted = formatValue(currentValue, false);

      // Always update DOM to show formatted value
      if (formatted !== currentValue) {
        inputRef.current.value = formatted;
      }

      // Update parent
      onFieldChange(fieldName, contextRef, formatted || currentValue || '', item.qname);
    }
  }, [formatValue, fieldName, contextRef, onFieldChange, item.qname]);

  // Prevent non-numeric characters from being entered (only allow numbers, decimal, minus, comma)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputRef.current?.readOnly) return;
    
    // Allow: Backspace, Delete, Tab, Escape, Enter, Arrow keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88, 90].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Home, End
    if ([35, 36].includes(e.keyCode)) {
      return;
    }
    
    // Allow: numbers (0-9) on main keyboard and numpad
    if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    
    // Allow: decimal point (.) and comma (,)
    if ([190, 188, 110].includes(e.keyCode)) {
      return;
    }
    
    // Allow: minus sign (-) for negative numbers
    if ([189, 109].includes(e.keyCode)) {
      return;
    }
    
    // Prevent all other keys (including alphabets)
    e.preventDefault();
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // DON'T format while typing - just save raw value
      // Formatting ONLY happens on blur
      onFieldChange(fieldName, contextRef, newValue || '', item.qname);
    }, 300);
  }, [fieldName, contextRef, onFieldChange, item.qname]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="footnote-display-group">
      {onFootnoteClick ? (
        <div
          className="footnote-anchor"
          data-field-id={fieldId}
          data-field-name={item.name}
          data-field-label={item.standard_label}
          data-context-ref={contextRef}
          data-qname={item.qname}
          data-readonly={isReadonly ? 'true' : 'false'}
        />
      ) : (
        <span className="footnote-button-spacer"></span>
      )}
      <input
        ref={inputRef}
        type="text"
        className="form-control financial-input"
        name={fieldName}
        data-qname={item.qname}
        data-context-ref={contextRef}
        data-note-member={specialAttributes['data-note-member'] || ''}
        data-calculation-target={item.name}
        data-no-fill={item.validation?.no_fill || 'N'}
        data-balance={item.properties?.balance}
        data-type={item.properties?.data_type}
        data-period-type={item.properties?.period_type}
        data-field-id={fieldId}
        defaultValue={initialValue}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={computedStyle}
        {...specialAttributes}
      />
    </div>
  );
};

interface LineItemProps {
  item: any;
  members: any[];
  periodInfo: PeriodInfo;
  memberSuffix: string;
  negativeTargets: string[];
  formData: Record<string, string>;
  onFieldChange: (name: string, contextRef: string, value: string, qname?: string) => void;
  level: number;
  getShareOccurrence?: (itemName: string, itemKey: string) => number;
  itemIndex?: number;
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
}

const NoteLineItem: React.FC<LineItemProps> = ({
  item,
  members,
  periodInfo,
  memberSuffix,
  negativeTargets,
  formData,
  onFieldChange,
  level,
  getShareOccurrence,
  itemIndex = 0,
  onFootnoteClick
}) => {
  // Share fields that need occurrence tracking
  const shareFields = ['AmountOfSharesOutstanding', 'NumberOfSharesOutstanding', 'AmountOfSharesIssuedAndFullyPaidOutstanding'];
  const isShareField = shareFields.includes(item.name);

  // Create stable unique key for this item (like SOFPTable)
  const itemKey = `${level}_${itemIndex}`;

  // Get occurrence for share fields (stable across renders)
  const shareOccurrence = isShareField && getShareOccurrence ?
    getShareOccurrence(item.name, itemKey) : null;

  // Handle abstract items (section headers)
  if (item.properties?.abstract) {
    return (
      <>
        <tr>
          <td
            className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}
            style={{ paddingLeft: `${(level + 1) * 15}px` }}
            colSpan={members.length + 2}
          >
            {item.standard_label}
          </td>
        </tr>
        {item.structure?.children?.map((child: any, index: number) => (
          <NoteLineItem
            key={`${child.name}_${index}`}
            item={child}
            members={members}
            periodInfo={periodInfo}
            memberSuffix={memberSuffix}
            negativeTargets={negativeTargets}
            formData={formData}
            onFieldChange={onFieldChange}
            level={level + 1}
            getShareOccurrence={getShareOccurrence}
            itemIndex={index}
            onFootnoteClick={onFootnoteClick}
          />
        ))}
      </>
    );
  }

  return (
    <>
      <tr>
        <td
          className={`
            ${level === 0 ? 'table-title-0 c-black' : ''}
            ${level === 1 ? 'table-title-1 c-theme-600' : ''}
            ${level === 2 ? 'table-title-2 c-theme-500' : ''}
            ${level === 3 ? 'table-title-3 c-dark' : ''}
            ${level >= 4 ? 'table-title-4 c-light' : ''}
            ${negativeTargets.includes(item.name) ? 'negative-indicator' : ''}
          `}
          style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
          title={negativeTargets.includes(item.name) ? 'Subtracted from total' : ''}
        >
          {item.standard_label}
          {shareOccurrence && <span className="text-gray-600 text-sm"> ({shareOccurrence === 1 ? 'opening' : 'closing'})</span>}
        </td>

        {/* Render columns for each member */}
        {members.map((member, memberIndex) => {
          const isOpening = shareOccurrence === 1;
          const contextRef = getContextRef(item, periodInfo, memberSuffix, member.name, isOpening);
          const fieldName = item.name;
          const fieldId = `${contextRef}_${item.qname}`;
          // Use qname + contextRef as primary key
          const dataKey = `${item.qname}_${contextRef}`;
          const fieldValue = formData[dataKey] || '';

          const specialAttributes: Record<string, string> = {
            'data-note-member': member.name
          };
          if (shareOccurrence) {
            specialAttributes['data-share-source'] = shareOccurrence === 1 ? 'opening' : 'closing';
          }

          return (
            <td key={memberIndex} className="table-input">
              <div className={`
                ${level === 0 ? 'table-title-0 c-black' : ''}
                ${level === 1 ? 'table-title-1 c-theme-600' : ''}
                ${level === 2 ? 'table-title-2 c-theme-500' : ''}
                ${level === 3 ? 'table-title-3 c-dark' : ''}
                ${level >= 4 ? 'table-title-4 c-light' : ''}
              `}>
                {(item.properties?.data_type === 'monetary' || item.properties?.data_type === 'shares') && (
                  <NoteFinancialInput
                    item={item}
                    contextRef={contextRef}
                    fieldId={fieldId}
                    fieldName={fieldName}
                    isReadonly={false}
                    value={fieldValue}
                    onFieldChange={onFieldChange}
                    specialAttributes={specialAttributes}
                    onFootnoteClick={onFootnoteClick}
                  />
                )}
              </div>
            </td>
          );
        })}

        {/* Total column */}
        <td className="table-input">
          {(() => {
            const isOpening = shareOccurrence === 1;
            const contextRef = getContextRef(item, periodInfo, memberSuffix, undefined, isOpening);
            const fieldName = item.name;
            const fieldId = `${contextRef}_${item.qname}_total`;
            // Use qname + contextRef as primary key
            const dataKey = `${item.qname}_${contextRef}`;
            const fieldValue = formData[dataKey] || '';

            const specialAttributes: Record<string, string> = {
              'data-note-total': 'true'
            };
            if (shareOccurrence) {
              specialAttributes['data-share-source'] = shareOccurrence === 1 ? 'opening' : 'closing';
            }

            return (
              <div className={`
                ${level === 0 ? 'table-title-0 c-black' : ''}
                ${level === 1 ? 'table-title-1 c-theme-600' : ''}
                ${level === 2 ? 'table-title-2 c-theme-500' : ''}
                ${level === 3 ? 'table-title-3 c-dark' : ''}
                ${level >= 4 ? 'table-title-4 c-light' : ''}
              `}>
                {(item.properties?.data_type === 'monetary' || item.properties?.data_type === 'shares') && (
                  <NoteFinancialInput
                    item={item}
                    contextRef={contextRef}
                    fieldId={fieldId}
                    fieldName={fieldName}
                    isReadonly={true}
                    value={fieldValue}
                    onFieldChange={onFieldChange}
                    specialAttributes={specialAttributes}
                    onFootnoteClick={undefined}
                  />
                )}
              </div>
            );
          })()}
        </td>
      </tr>
      {item.structure?.children?.map((child: any, index: number) => (
        <NoteLineItem
          key={`${child.name}_${index}`}
          item={child}
          members={members}
          periodInfo={periodInfo}
          memberSuffix={memberSuffix}
          negativeTargets={negativeTargets}
          formData={formData}
          onFieldChange={onFieldChange}
          level={level + 1}
          getShareOccurrence={getShareOccurrence}
          itemIndex={index}
          onFootnoteClick={onFootnoteClick}
        />
      ))}
    </>
  );
};

export const NoteShareRelatedPartyTable: React.FC<NoteShareRelatedPartyTableProps> = React.memo(({
  section,
  companyInfo,
  negativeTargets = [],
  formData: parentFormData = {},
  onFootnoteClick
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  // Use parentFormData directly (single source of truth from EditFilings ref)
  const formData = parentFormData;

  // Share occurrence counter (like SOFPTable's occurrence tracking)
  const shareOccurrenceCounterRef = useRef<Map<string, number>>(new Map());

  // Get share occurrence count (stable - doesn't change on re-renders)
  const getShareOccurrence = useCallback((itemName: string, itemKey: string): number => {
    // Use a unique key that includes item position to ensure stability
    const uniqueKey = `${itemName}_${itemKey}`;

    if (!shareOccurrenceCounterRef.current.has(uniqueKey)) {
      // Only increment the counter for this item name when we see a new unique key
      const itemCount = Array.from(shareOccurrenceCounterRef.current.keys())
        .filter(key => key.startsWith(`${itemName}_`)).length + 1;

      shareOccurrenceCounterRef.current.set(uniqueKey, itemCount);
    }

    return shareOccurrenceCounterRef.current.get(uniqueKey)!;
  }, []);

  // Use Note-specific HyperFormula hook (only horizontal sums)
  const localHyperFormula = useHyperFormulaNote({ sections: { [section.name]: section } });

  // Extract members and line items
  const { members, lineItems } = useMemo(() => {
    const axis = section.structure?.children?.[0];
    const items = section.structure?.children?.[1];

    let allMembers: any[] = [];

    // Collect members from axis structure
    if (axis?.structure?.children?.[1]?.structure?.children?.[0]) {
      const root = axis.structure.children[1].structure.children[0];
      allMembers = collectMembers(root);
    }

    // Limit to first 100 members
    allMembers = allMembers.slice(0, 100);

    // Get line items
    const lineItemsList = items?.structure?.children || [];

    return { members: allMembers, lineItems: lineItemsList };
  }, [section]);

  // Get period information
  const getPeriodInfo = useCallback((): PeriodInfo => {
    // Try reporting_period first (from EditFilings)
    if (companyInfo.reporting_period) {
      return {
        opening: formatDateForContext(companyInfo.reporting_period.opening),
        start: formatDateForContext(companyInfo.reporting_period.start),
        end: formatDateForContext(companyInfo.reporting_period.end)
      };
    }

    // Try currentPeriod (fallback)
    if (companyInfo.currentPeriod) {
      return {
        opening: formatDateForContext(companyInfo.currentPeriod.opening),
        start: formatDateForContext(companyInfo.currentPeriod.start),
        end: formatDateForContext(companyInfo.currentPeriod.end)
      };
    }

    return {
      opening: '20240430',
      start: '20240501',
      end: '20250430'
    };
  }, [companyInfo]);

  const handleFieldChange = useCallback((name: string, contextRef: string, value: string, qname?: string) => {
    // Use qname + contextRef as primary key if provided
    // const dataKey = qname ? `${qname}_${contextRef}` : `${name}_${contextRef}`;
    // setFormData(prev => ({ ...prev, [dataKey]: value })); // REMOVED local state update

    if (localHyperFormula.isReady && localHyperFormula.handleFieldChange) {
      localHyperFormula.handleFieldChange(name, contextRef, value);
    }

    // Note: We don't need to call setFormData because the parent component (EditFilings)
    // updates the ref, and we read directly from parentFormData (which is the ref)
    // However, since parentFormData is a ref.current passed down, it might not trigger re-render
    // But since inputs are uncontrolled (defaultValue), they don't need re-render for value updates
  }, [localHyperFormula.isReady, localHyperFormula.handleFieldChange]);

  // Render a single table for current period only
  const renderSingleTable = useCallback((
    periodInfo: PeriodInfo,
    memberSuffix: string
  ) => {
    // Create table-specific getShareOccurrence (include table key for separate counting per table)
    const tableKey = `${memberSuffix}_${periodInfo.start}_${periodInfo.end}`;

    const getTableShareOccurrence = (itemName: string, itemKey: string): number => {
      const uniqueKey = `${tableKey}_${itemName}_${itemKey}`;

      if (!shareOccurrenceCounterRef.current.has(uniqueKey)) {
        // Count occurrences of this item name within this table only
        const itemCount = Array.from(shareOccurrenceCounterRef.current.keys())
          .filter(key => key.startsWith(`${tableKey}_${itemName}_`)).length + 1;

        shareOccurrenceCounterRef.current.set(uniqueKey, itemCount);
      }

      return shareOccurrenceCounterRef.current.get(uniqueKey)!;
    };

    return (
      <div key={`${memberSuffix}_${periodInfo.start}_${periodInfo.end}`} className="note-table-section">
        <h3 className="section-header">
          <span>
            Period: {formatDateForDisplay(periodInfo.start)} to {formatDateForDisplay(periodInfo.end)}, {memberSuffix}
          </span>
          {(memberSuffix === 'SeparateMember' || memberSuffix === 'ConsolidatedMember') && (
            <button
              type="button"
              className="table-toggle-button"
              onClick={(e) => {
                e.preventDefault();
                const sectionEl = (e.currentTarget as HTMLButtonElement).closest('.note-table-section') as HTMLElement | null;
                if (sectionEl) toggleColumnVisibilityForSection(sectionEl);
              }}
            >
              Show All Columns
            </button>
          )}
        </h3>
        <div className="card-table" style={{ overflow: 'auto' }}>
          <table
            className="table-element sum-calculation-table"
            data-datatable-table="true"
            data-period-start={formatDateForContext(periodInfo.start)}
            data-period-end={formatDateForContext(periodInfo.end)}
            data-member-suffix={memberSuffix}
          >
            <thead>
              <tr>
                <th>
                  <span className="sort">
                    <span className="sort-label">Components</span>
                  </span>
                </th>
                {members.map((member, index) => (
                  <th key={index}>
                    <span className="sort">
                      <span className="sort-label">{member.standard_label}</span>
                    </span>
                  </th>
                ))}
                <th>
                  <span className="sort">
                    <span className="sort-label">Total</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item: any, index: number) => (
                <NoteLineItem
                  key={`${item.name}_${item.qname}_${index}`}
                  item={item}
                  members={members}
                  periodInfo={periodInfo}
                  memberSuffix={memberSuffix}
                  negativeTargets={negativeTargets}
                  formData={formData}
                  onFieldChange={handleFieldChange}
                  level={0}
                  getShareOccurrence={getTableShareOccurrence}
                  itemIndex={index}
                  onFootnoteClick={onFootnoteClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [members, lineItems, negativeTargets, formData, handleFieldChange, shareOccurrenceCounterRef]);

  // Default behavior: hide empty member columns once Note calculations are ready
  useEffect(() => {
    const run = () => {
      const container = rootRef.current;
      if (!container) return;
      const targetTables = container.querySelectorAll(
        'table.sum-calculation-table[data-member-suffix="SeparateMember"], table.sum-calculation-table[data-member-suffix="ConsolidatedMember"]'
      ) as NodeListOf<HTMLTableElement>;
      targetTables.forEach((t) => applyHideEmptyColumns(t));
    };

    const onComplete = () => setTimeout(run, 50);
    document.addEventListener('noteCalculationsComplete', onComplete);
    return () => document.removeEventListener('noteCalculationsComplete', onComplete);
  }, []);

  // Determine which tables to render (ONLY CURRENT YEAR)
  const tablesToRender = useMemo(() => {
    const currentPeriod = getPeriodInfo();
    const tables: JSX.Element[] = [];

    if (companyInfo.consolidated === 'Y') {
      tables.push(renderSingleTable(currentPeriod, 'ConsolidatedMember'));
      tables.push(renderSingleTable(currentPeriod, 'SeparateMember'));
    } else {
      tables.push(renderSingleTable(currentPeriod, 'SeparateMember'));
    }

    return tables;
  }, [companyInfo, getPeriodInfo, renderSingleTable]);

  return (
    <div ref={rootRef} className="card">
      <div className="card-body">
        {tablesToRender}
      </div>
    </div>
  );
});

